// SPDX-License-Identifier: AGPL-3.0-or-later

// Progression badges: each category climbs through tiers as a lifetime number grows.
// Names, thresholds and art are LafON's own. Tier colours follow one shared ladder so a
// "sapphire" step means the same rank in every category.

import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export type AchievementCategoryId = 'moon' | 'pro' | 'playtime' | 'atlas' | 'crystal';
export type AchievementUnit = 'days' | 'hours' | 'games';

export interface AchievementTier {
	threshold: number;
	name: MessageDescriptor;
}

export interface AchievementCategory {
	id: AchievementCategoryId;
	title: MessageDescriptor;
	/** What the badge recognises. */
	about: MessageDescriptor;
	/** How to climb: shown above the tier ladder. */
	hint: MessageDescriptor;
	/** Optional call to action shown under the ladder. */
	cta?: 'premium' | 'boost';
	unit: AchievementUnit;
	tiers: ReadonlyArray<AchievementTier>;
}

const t = (threshold: number, name: MessageDescriptor): AchievementTier => ({threshold, name});

export const ACHIEVEMENT_CATEGORIES: ReadonlyArray<AchievementCategory> = [
	{
		id: 'moon',
		title: msg({message: 'Moon Phase', comment: 'Badge category based on account age.'}),
		about: msg({
			message: 'Marks how long you have been part of LafON. The moon takes on a new colour as your account ages.',
			comment: 'What the account age badge recognises.',
		}),
		hint: msg({
			message: 'Every day you spend on LafON moves the moon forward. Time does the rest.',
			comment: 'How to level up the account age badge.',
		}),
		unit: 'days',
		tiers: [
			t(7, msg({message: 'New Moon', comment: 'Account age badge tier (1 week).'})),
			t(30, msg({message: 'Crescent', comment: 'Account age badge tier (1 month).'})),
			t(90, msg({message: 'First Quarter', comment: 'Account age badge tier (3 months).'})),
			t(180, msg({message: 'Gibbous', comment: 'Account age badge tier (6 months).'})),
			t(365, msg({message: 'Full Moon', comment: 'Account age badge tier (1 year).'})),
			t(730, msg({message: 'Halo', comment: 'Account age badge tier (2 years).'})),
			t(1095, msg({message: 'Eclipse', comment: 'Account age badge tier (3 years).'})),
			t(1825, msg({message: 'Supermoon', comment: 'Account age badge tier (5 years).'})),
		],
	},
	{
		id: 'pro',
		title: msg({message: 'Pro Journey', comment: 'Badge category based on continuous Pro subscription time.'}),
		about: msg({
			message: 'Given to Pro members from their first day. It levels up the longer the subscription stays active.',
			comment: 'What the Pro tenure badge recognises.',
		}),
		cta: 'premium',
		hint: msg({
			message: 'Keep your Pro subscription active to keep the flame growing.',
			comment: 'How to level up the Pro tenure badge.',
		}),
		unit: 'days',
		tiers: [
			t(0, msg({message: 'Spark', comment: 'Pro tenure badge tier (first day).'})),
			t(30, msg({message: 'Flame', comment: 'Pro tenure badge tier (1 month).'})),
			t(90, msg({message: 'Torch', comment: 'Pro tenure badge tier (3 months).'})),
			t(180, msg({message: 'Beacon', comment: 'Pro tenure badge tier (6 months).'})),
			t(365, msg({message: 'Star', comment: 'Pro tenure badge tier (1 year).'})),
			t(730, msg({message: 'Constellation', comment: 'Pro tenure badge tier (2 years).'})),
			t(1095, msg({message: 'Nebula', comment: 'Pro tenure badge tier (3 years).'})),
			t(1825, msg({message: 'Galaxy', comment: 'Pro tenure badge tier (5 years).'})),
		],
	},
	{
		id: 'playtime',
		title: msg({message: 'Game Time', comment: 'Badge category based on total detected game time.'}),
		about: msg({
			message: 'Counts the hours of PC games detected by the LafON desktop app.',
			comment: 'What the game time badge recognises.',
		}),
		hint: msg({
			message: 'Play detectable PC games while the LafON desktop app is open to fill the hourglass.',
			comment: 'How to level up the game time badge.',
		}),
		unit: 'hours',
		tiers: [
			t(1, msg({message: 'Warm-up', comment: 'Game time badge tier (1 hour).'})),
			t(10, msg({message: 'In Rhythm', comment: 'Game time badge tier (10 hours).'})),
			t(50, msg({message: 'Steady', comment: 'Game time badge tier (50 hours).'})),
			t(100, msg({message: 'Devoted', comment: 'Game time badge tier (100 hours).'})),
			t(250, msg({message: 'Adept', comment: 'Game time badge tier (250 hours).'})),
			t(500, msg({message: 'Veteran', comment: 'Game time badge tier (500 hours).'})),
			t(1000, msg({message: 'Champion', comment: 'Game time badge tier (1,000 hours).'})),
			t(2500, msg({message: 'Legend', comment: 'Game time badge tier (2,500 hours).'})),
			t(5000, msg({message: 'Timeless', comment: 'Game time badge tier (5,000 hours).'})),
		],
	},
	{
		id: 'atlas',
		title: msg({message: 'Game Atlas', comment: 'Badge category based on the number of different games played.'}),
		about: msg({
			message: 'Counts the different PC games detected by the LafON desktop app.',
			comment: 'What the game variety badge recognises.',
		}),
		hint: msg({
			message: 'Try new detectable PC games with the desktop app open to chart more of the map.',
			comment: 'How to level up the game variety badge.',
		}),
		unit: 'games',
		tiers: [
			t(2, msg({message: 'Compass', comment: 'Game variety badge tier (2 games).'})),
			t(5, msg({message: 'Trail', comment: 'Game variety badge tier (5 games).'})),
			t(10, msg({message: 'Route', comment: 'Game variety badge tier (10 games).'})),
			t(20, msg({message: 'Map', comment: 'Game variety badge tier (20 games).'})),
			t(35, msg({message: 'Voyager', comment: 'Game variety badge tier (35 games).'})),
			t(50, msg({message: 'Cartographer', comment: 'Game variety badge tier (50 games).'})),
			t(75, msg({message: 'Atlas', comment: 'Game variety badge tier (75 games).'})),
			t(100, msg({message: 'Horizon', comment: 'Game variety badge tier (100 games).'})),
		],
	},
	{
		id: 'crystal',
		title: msg({message: 'Server Booster', comment: 'Badge category based on how long the user has boosted a server.'}),
		about: msg({
			message: 'Given to members who boost a community, from the very first day. It grows as the boost continues.',
			comment: 'What the server boost badge recognises.',
		}),
		cta: 'boost',
		hint: msg({
			message: 'Keep boosting a server to keep climbing the levels.',
			comment: 'How to level up the server boost tenure badge.',
		}),
		unit: 'days',
		tiers: [
			t(0, msg({message: 'Droplet', comment: 'Boost tenure badge tier (first day).'})),
			t(30, msg({message: 'Glimmer', comment: 'Boost tenure badge tier (1 month).'})),
			t(60, msg({message: 'Crystal', comment: 'Boost tenure badge tier (2 months).'})),
			t(90, msg({message: 'Prism', comment: 'Boost tenure badge tier (3 months).'})),
			t(180, msg({message: 'Quartz', comment: 'Boost tenure badge tier (6 months).'})),
			t(270, msg({message: 'Amethyst', comment: 'Boost tenure badge tier (9 months).'})),
			t(365, msg({message: 'Sapphire', comment: 'Boost tenure badge tier (1 year).'})),
			t(540, msg({message: 'Emerald', comment: 'Boost tenure badge tier (18 months).'})),
			t(730, msg({message: 'Diamond', comment: 'Boost tenure badge tier (2 years).'})),
		],
	},
];

export type AchievementRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export const RARITY_LABELS: Record<AchievementRarity, MessageDescriptor> = {
	common: msg({message: 'Common', comment: 'Badge rarity label.'}),
	uncommon: msg({message: 'Uncommon', comment: 'Badge rarity label.'}),
	rare: msg({message: 'Rare', comment: 'Badge rarity label.'}),
	epic: msg({message: 'Epic', comment: 'Badge rarity label.'}),
	legendary: msg({message: 'Legendary', comment: 'Badge rarity label.'}),
};

/** Rarity grows with how far up its ladder a tier sits, so long ladders don't cheapen the top. */
export function getTierRarity(category: AchievementCategory, tierIndex: number): AchievementRarity {
	const ratio = (tierIndex + 1) / category.tiers.length;
	if (ratio >= 0.99) return 'legendary';
	if (ratio >= 0.75) return 'epic';
	if (ratio >= 0.5) return 'rare';
	if (ratio >= 0.25) return 'uncommon';
	return 'common';
}

export interface AchievementProgress {
	category: AchievementCategory;
	value: number;
	/** Highest reached tier, or -1 when the first one is still locked. */
	tierIndex: number;
	/** When the current tier was reached, when it can be derived from a start date. */
	unlockedAt: Date | null;
	/** Start of the measured period (account creation, Pro or boost start). */
	since: Date | null;
}

export interface AchievementInputs {
	accountCreatedAt: Date;
	premiumSince: Date | null;
	playMs: number | null;
	gamesPlayed: number | null;
	boostingSince: Date | null;
	now?: Date;
}

const DAY_MS = 86_400_000;

/** Whole days since `date`; -1 when there is no start date, so day-0 tiers stay locked. */
function daysSince(date: Date | null, now: Date): number {
	return date ? Math.max(0, Math.floor((now.getTime() - date.getTime()) / DAY_MS)) : -1;
}

export function computeAchievements(inputs: AchievementInputs): Array<AchievementProgress> {
	const now = inputs.now ?? new Date();
	const values: Record<AchievementCategoryId, {value: number; since: Date | null}> = {
		moon: {value: daysSince(inputs.accountCreatedAt, now), since: inputs.accountCreatedAt},
		pro: {value: daysSince(inputs.premiumSince, now), since: inputs.premiumSince},
		playtime: {value: Math.floor((inputs.playMs ?? 0) / 3_600_000), since: null},
		atlas: {value: inputs.gamesPlayed ?? 0, since: null},
		crystal: {value: daysSince(inputs.boostingSince, now), since: inputs.boostingSince},
	};
	return ACHIEVEMENT_CATEGORIES.map((category) => {
		const {value, since} = values[category.id];
		let tierIndex = -1;
		category.tiers.forEach((tier, index) => {
			if (value >= tier.threshold) tierIndex = index;
		});
		const unlockedAt =
			tierIndex >= 0 && since && category.unit === 'days'
				? new Date(since.getTime() + category.tiers[tierIndex].threshold * DAY_MS)
				: null;
		return {category, value, tierIndex, unlockedAt, since};
	});
}
